/**
 * Fit a map into a print slot after orientation (#266 / preview parity).
 * Math is post-rotation: CSS must not object-fit then rotate.
 */
import type { ZwMapFigure } from "./zwConfig";

export type MapPlacementFit = {
  /** Unrotated layout width of the image element (px). */
  readonly layoutWidth: number;
  /** Unrotated layout height of the image element (px). */
  readonly layoutHeight: number;
  /** Visual width after rotation (px). */
  readonly visualWidth: number;
  /** Visual height after rotation (px). */
  readonly visualHeight: number;
};

/**
 * Size an image so that after `orientation` it contain/cover-fits `slotW`×`slotH`.
 * Intrinsic size is the map’s natural (unrotated) pixel size.
 */
export function mapPlacementFit(
  slotW: number,
  slotH: number,
  naturalW: number,
  naturalH: number,
  orientation: ZwMapFigure["orientation"],
  objectFit: ZwMapFigure["objectFit"],
): MapPlacementFit {
  const boxW = Math.max(1, slotW);
  const boxH = Math.max(1, slotH);
  const srcW = Math.max(1, naturalW);
  const srcH = Math.max(1, naturalH);
  const quarter = orientation === 90 || orientation === 270;
  // After a quarter turn, the painted content swaps axes.
  const contentW = quarter ? srcH : srcW;
  const contentH = quarter ? srcW : srcH;
  const scale =
    objectFit === "cover"
      ? Math.max(boxW / contentW, boxH / contentH)
      : Math.min(boxW / contentW, boxH / contentH);
  const layoutWidth = srcW * scale;
  const layoutHeight = srcH * scale;
  return {
    layoutWidth,
    layoutHeight,
    visualWidth: contentW * scale,
    visualHeight: contentH * scale,
  };
}
